import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { GetUserEventPermissions, getUserDetails } from '../api/api';
import { getMobileDeviceId, isNativeApp } from '../utils/deviceIdentity';
import { isDemoMobile } from '../utils/demo';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const LS = {
  user: 'se_user',
  organiser: 'se_organiser',
  event: 'se_event',
  eventPerms: 'se_eventPerms',
  creds: 'se_creds',
  viewAsSession: 'se_viewAsSession',
  mustChangePw: 'se_mustChangePw',
};

const read = (k) => {
  try { return JSON.parse(localStorage.getItem(k)); } catch { return null; }
};
const write = (k, v) => {
  if (v == null) localStorage.removeItem(k);
  else localStorage.setItem(k, JSON.stringify(v));
};

// Saved login credentials for "keep me signed in" (re-verified against the API
// on every app start). Plain-text — matches the app's current auth model.
export const getSavedCreds = () => read(LS.creds);
// Credentials for THIS session: localStorage when "keep me signed in", else
// sessionStorage (gone when the tab closes). DeviceGuard re-verifies with these.
export const getSessionCreds = () => {
  const saved = read(LS.creds);
  if (saved) return saved;
  try { return JSON.parse(sessionStorage.getItem(LS.creds)); } catch { return null; }
};
const writeCreds = (creds, keep) => {
  write(LS.creds, keep ? creds : null);
  try { if (keep) sessionStorage.removeItem(LS.creds); else if (creds) sessionStorage.setItem(LS.creds, JSON.stringify(creds)); } catch { /* ignore */ }
};

// Roles, in precedence order. Permissions are now PER-EVENT: the granular Can*
// bits live on Evt_UserEventsMapping (UserId + EventId), loaded as `eventPerms`
// once an event is selected. Evt_UserOrganiserMapping stays as the organiser
// access record + organiser-wide IsAdmin (Owner / Event Admin over all events).
//   1. Super Admin   — Evt_Users.SuperAdmin = 1 (platform-wide, no perm rows)
//   2. Owner         — organiser.OrganiserMasterUser === user.UserId
//   3. Event Admin   — organiser-wide IsAdmin, OR per-event IsAdmin on the
//                      selected event's Evt_UserEventsMapping row
//   4. User          — a plain mapped member, limited to its per-event Can* bits
export const getRoleLabel = (user, organiser, eventPerms) => {
  if (user?.SuperAdmin === 1) return 'Super Admin';
  if (organiser && user && organiser.OrganiserMasterUser === user.UserId) return 'Event Admin';
  if (organiser?.IsAdmin === 1 || eventPerms?.IsAdmin === 1) return 'Event Admin';
  return 'Member';
};

export const getRoleKey = (user, organiser, eventPerms) => {
  if (user?.SuperAdmin === 1) return 'superadmin';
  if (organiser && user && organiser.OrganiserMasterUser === user.UserId) return 'eventadmin';
  if (organiser?.IsAdmin === 1 || eventPerms?.IsAdmin === 1) return 'eventadmin';
  return 'member';
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => read(LS.user));
  const [organiser, setOrganiser] = useState(() => read(LS.organiser));
  const [event, setEventState] = useState(() => read(LS.event));
  // The signed-in user's permission row for the selected event (per-event Can*
  // bits + per-event IsAdmin). Null until an event is picked and the row loads.
  const [eventPerms, setEventPerms] = useState(() => read(LS.eventPerms));
  const [viewAsSession, setViewAsSession] = useState(() => read(LS.viewAsSession));
  const [ready, setReady] = useState(false);
  // Set at sign-in when the password is still the default '1234'; the router
  // forces /change-password until cleared (see App.jsx <Protected>).
  const [mustChangePassword, setMustChangePw] = useState(() => !!read(LS.mustChangePw));

  const clearLocalSession = useCallback(() => {
    setUser(null); setOrganiser(null); setEventState(null); setEventPerms(null);
    setViewAsSession(null); setMustChangePw(false);
    write(LS.user, null); write(LS.organiser, null); write(LS.event, null);
    write(LS.eventPerms, null); writeCreds(null, true); write(LS.viewAsSession, null);
    write(LS.mustChangePw, null);
  }, []);

  // Native sessions are valid only while this phone still owns MobileDeviceId.
  // Check before rendering the restored session, and again whenever the app is
  // brought back to the foreground.
  const validateNativeDevice = useCallback(async () => {
    const storedUser = read(LS.user);
    if (read(LS.viewAsSession)) return true;
    if (!isNativeApp() || !storedUser?.MobileNumber) return true;
    if (isDemoMobile(storedUser.MobileNumber)) return true; // demo account never binds
    try {
      const [users, deviceId] = await Promise.all([
        getUserDetails({ MobileNumber: storedUser.MobileNumber }),
        getMobileDeviceId(),
      ]);
      const dbUser = Array.isArray(users) ? users[0] : null;
      // MobileDeviceId is either a legacy raw hardware id or "<token>.<hardware id>"
      // (server-issued token, see utils/authFlow.js) — compare the hardware part.
      const stored = String(dbUser?.MobileDeviceId || '');
      const storedHw = stored.includes('.') ? stored.slice(stored.indexOf('.') + 1) : stored;
      if (!dbUser || !stored || storedHw !== String(deviceId)) {
        clearLocalSession();
        return false;
      }
      setUser(dbUser);
      write(LS.user, dbUser);
      return true;
    } catch {
      // A temporary network failure should not destroy an otherwise valid local
      // session. The next foreground event will try again.
      return true;
    }
  }, [clearLocalSession]);

  useEffect(() => {
    let cancelled = false;
    validateNativeDevice().finally(() => { if (!cancelled) setReady(true); });

    let appHandle;
    if (isNativeApp()) {
      const App = window.Capacitor?.Plugins?.App;
      if (App?.addListener) {
        Promise.resolve(App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) validateNativeDevice();
        })).then(handle => { appHandle = handle; });
      }
    }
    return () => {
      cancelled = true;
      if (appHandle?.remove) appHandle.remove();
    };
  }, [validateNativeDevice]);

  // Role helpers used for hard gating (not just can() bits):
  //   isSuperAdmin — platform operator; the only role allowed to add Events.
  //   isOwner      — the user who owns the selected organiser (created it).
  const isSuperAdmin = user?.SuperAdmin === 1;
  const isOwner = !!(organiser && user && organiser.OrganiserMasterUser === user.UserId);
  // "Org-level admin" = Super Admin or organiser-wide IsAdmin (Owner / Event
  // Admin over every event). These bypass all per-event checks.
  const isOrgAdmin = isSuperAdmin || organiser?.IsAdmin === 1;
  // isAdmin also true when the user is admin of the *selected* event.
  const isAdmin = isOrgAdmin || eventPerms?.IsAdmin === 1;

  // Load the per-event permission row whenever the user or selected event
  // changes. Org-admins skip the fetch (they pass everything anyway).
  useEffect(() => {
    if (!user || !event?.EventId) { setEventPerms(null); write(LS.eventPerms, null); return; }
    if (isOrgAdmin) { setEventPerms(null); write(LS.eventPerms, null); return; }
    let cancelled = false;
    GetUserEventPermissions({ EventId: event.EventId, UserId: user.UserId })
      .then(rows => {
        if (cancelled) return;
        const row = Array.isArray(rows) ? rows[0] : (rows || null);
        setEventPerms(row || null); write(LS.eventPerms, row || null);
      })
      .catch(() => { if (!cancelled) { setEventPerms(null); write(LS.eventPerms, null); } });
    return () => { cancelled = true; };
  }, [user, event, isOrgAdmin]);

  // Event-scoped permission check:
  //   true if Super Admin / org-wide admin, OR admin of the selected event,
  //   OR the selected event's permission row grants the specific bit.
  const can = useCallback((key) => {
    if (isOrgAdmin) return true;
    if (eventPerms?.IsAdmin === 1) return true;
    return eventPerms?.[key] === 1;
  }, [isOrgAdmin, eventPerms]);

  // `creds` ({ mobile, password }) always given; `keep` → remembered across
  // restarts (localStorage), else session-only. `mustChangePw` forces /change-password.
  const login = (u, org, creds, keep = false, mustChangePw = false) => {
    setUser(u); write(LS.user, u);
    setOrganiser(org); write(LS.organiser, org);
    setEventState(null); write(LS.event, null);
    setEventPerms(null); write(LS.eventPerms, null);
    writeCreds(creds, keep);
    setMustChangePw(mustChangePw); write(LS.mustChangePw, mustChangePw ? 1 : null);
  };
  // After a password change: keep the session creds in step and lift the block.
  const passwordChanged = (newPassword) => {
    const c = getSessionCreds();
    if (c) writeCreds({ ...c, password: newPassword }, !!read(LS.creds));
    setMustChangePw(false); write(LS.mustChangePw, null);
  };
  const selectOrganiser = (org) => {
    setOrganiser(org); write(LS.organiser, org);
    setEventState(null); write(LS.event, null);
    setEventPerms(null); write(LS.eventPerms, null);
  };
  const selectEvent = (ev) => {
    setEventState(ev); write(LS.event, ev);
    setEventPerms(null); write(LS.eventPerms, null); // refetched by the effect
  };
  const logout = () => {
    clearLocalSession();
  };
  const updateUser = (u) => { setUser(u); write(LS.user, u); };
  // Keep the session credentials in step after a profile mobile/password edit.
  const updateSavedCreds = (creds) => writeCreds(creds, !!read(LS.creds));
  const startViewAs = (targetUser, targetOrganiser) => {
    if (!targetUser) return;
    const existing = read(LS.viewAsSession);
    const session = existing || {
      user,
      organiser,
      event,
      eventPerms,
      creds: read(LS.creds),
    };
    setViewAsSession(session);
    write(LS.viewAsSession, session);
    setUser(targetUser); write(LS.user, targetUser);
    setOrganiser(targetOrganiser || null); write(LS.organiser, targetOrganiser || null);
    setEventState(null); write(LS.event, null);
    setEventPerms(null); write(LS.eventPerms, null);
  };
  const stopViewAs = () => {
    const session = viewAsSession || read(LS.viewAsSession);
    if (!session?.user) return;
    setUser(session.user); write(LS.user, session.user);
    setOrganiser(session.organiser || null); write(LS.organiser, session.organiser || null);
    setEventState(session.event || null); write(LS.event, session.event || null);
    setEventPerms(null); write(LS.eventPerms, null);
    if (session.creds) write(LS.creds, session.creds);
    setViewAsSession(null); write(LS.viewAsSession, null);
  };
  const roleLabel = getRoleLabel(user, organiser, eventPerms);
  const roleKey = getRoleKey(user, organiser, eventPerms);

  return (
    <AuthContext.Provider value={{
      user, organiser, event, eventPerms, ready, mustChangePassword,
      isAdmin, isOrgAdmin, isSuperAdmin, isOwner, can,
      roleLabel, roleKey, isViewingAs: !!viewAsSession, viewAsSession,
      login, selectOrganiser, selectEvent, logout, updateUser, updateSavedCreds, passwordChanged,
      startViewAs, stopViewAs,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
